import { useSyncExternalStore, useCallback } from 'react';
import { capture } from '../lib/posthog';

const STORAGE_KEY = 'melodiza:schedule-v1';
const HOURS = Array.from({ length: 18 }, (_, i) => `${String(i + 6).padStart(2, '0')}:00`);

// ─── Default & Helpers ─────────────────────────────────────────────────

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `set-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function defaultSchedule() {
  return { _v: 1, sets: [], lastLaunchedAt: undefined, engineState: 'idle' };
}

function isValidHour(hour) {
  return HOURS.includes(hour);
}

function parseFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultSchedule();
    const parsed = JSON.parse(raw);
    if (!parsed || parsed._v !== 1 || !Array.isArray(parsed.sets)) {
      return defaultSchedule();
    }
    // Validate and sanitize sets
    const sets = parsed.sets.map((s) => {
      if (!s || typeof s.id !== 'string') return null;
      const days = Array.isArray(s.days)
        ? s.days.filter((d) => typeof d === 'number' && d >= 0 && d <= 6)
        : [];
      const grid = {};
      if (s.grid && typeof s.grid === 'object') {
        for (const [hour, plId] of Object.entries(s.grid)) {
          if (isValidHour(hour) && (typeof plId === 'string' || plId === null)) {
            if (plId !== null) grid[hour] = plId;
          }
        }
      }
      return {
        id: s.id,
        days: [...new Set(days)].sort((a, b) => a - b),
        grid,
        createdAt: typeof s.createdAt === 'number' ? s.createdAt : Date.now(),
        updatedAt: typeof s.updatedAt === 'number' ? s.updatedAt : Date.now(),
      };
    }).filter(Boolean);
    return {
      _v: 1,
      sets: sortSets(sets),
      lastLaunchedAt: typeof parsed.lastLaunchedAt === 'number' ? parsed.lastLaunchedAt : undefined,
      engineState: parsed.engineState === 'armed' ? 'armed' : 'idle',
    };
  } catch {
    return defaultSchedule();
  }
}

function sortSets(sets) {
  return [...sets].sort((a, b) => {
    const minA = a.days.length > 0 ? Math.min(...a.days) : 7;
    const minB = b.days.length > 0 ? Math.min(...b.days) : 7;
    return minA - minB;
  });
}

// ─── Module-level shared store ─────────────────────────────────────────

let _state = parseFromStorage();
const _listeners = new Set();

function emit() {
  _listeners.forEach((l) => l());
}

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(_state));
  } catch (err) {
    if (typeof process === 'undefined' || process.env.NODE_ENV !== 'production') {
      console.error('[useSchedule] persist failed:', err);
    }
  }
}

function mutate(updater) {
  _state = updater(_state);
  persist();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('melodiza:schedule-changed'));
  }
  emit();
}

function subscribe(listener) {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

export function getSnapshot() {
  return _state;
}

// Cross-tab sync
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY) return;
    _state = parseFromStorage();
    emit();
  });
  window.addEventListener('melodiza:schedule-changed', () => {
    // same-tab notification (already mutated by mutate())
  });
}

// ─── Module-level mutations ────────────────────────────────────────────

export function addSet(days = []) {
  const newId = uuid();
  const safeDays = Array.isArray(days)
    ? [...new Set(days.filter((d) => typeof d === 'number' && d >= 0 && d <= 6))].sort((a, b) => a - b)
    : [];

  mutate((state) => {
    let sets = state.sets.map((s) => {
      if (safeDays.some((d) => s.days.includes(d))) {
        // auto-eject overlapping days from existing set
        return { ...s, days: s.days.filter((d) => !safeDays.includes(d)), updatedAt: Date.now() };
      }
      return s;
    });
    const newSet = { id: newId, days: safeDays, grid: {}, createdAt: Date.now(), updatedAt: Date.now() };
    sets = [...sets, newSet];
    return { ...state, sets: sortSets(sets) };
  });

  capture('schedule_set_created', { setId: newId, days: safeDays });
  return newId;
}

export function removeSet(setId) {
  mutate((state) => ({
    ...state,
    sets: sortSets(state.sets.filter((s) => s.id !== setId)),
  }));
}

export function toggleDay(setId, dayIndex) {
  if (typeof dayIndex !== 'number' || dayIndex < 0 || dayIndex > 6) return;
  mutate((state) => {
    const targetSet = state.sets.find((s) => s.id === setId);
    if (!targetSet) return state;

    const isAdding = !targetSet.days.includes(dayIndex);
    let sets = state.sets.map((s) => {
      if (s.id === setId) {
        const newDays = isAdding
          ? [...new Set([...s.days, dayIndex])].sort((a, b) => a - b)
          : s.days.filter((d) => d !== dayIndex);
        return { ...s, days: newDays, updatedAt: Date.now() };
      }
      if (isAdding && s.days.includes(dayIndex)) {
        // auto-eject from other set
        return { ...s, days: s.days.filter((d) => d !== dayIndex), updatedAt: Date.now() };
      }
      return s;
    });
    return { ...state, sets: sortSets(sets) };
  });
}

export function setCell(setId, hour, playlistId) {
  if (!isValidHour(hour)) return;
  mutate((state) => ({
    ...state,
    sets: state.sets.map((s) => {
      if (s.id !== setId) return s;
      const newGrid = { ...s.grid };
      if (playlistId === null || playlistId === undefined) {
        delete newGrid[hour];
      } else {
        newGrid[hour] = playlistId;
      }
      return { ...s, grid: newGrid, updatedAt: Date.now() };
    }),
  }));
  capture('schedule_grid_painted', { setId, hour });
}

export function paintCell(setId, hour, playlistId) {
  setCell(setId, hour, playlistId);
}

export function clearCell(setId, hour) {
  setCell(setId, hour, null);
}

export function markLaunched() {
  mutate((state) => ({
    ...state,
    lastLaunchedAt: Date.now(),
    engineState: 'armed',
  }));
  capture('schedule_launched');
}

export function setEngineState(engineState) {
  mutate((state) => ({ ...state, engineState }));
}

export function resetAll() {
  mutate(() => defaultSchedule());
}

export function getSetForDay(dayIndex) {
  return _state.sets.find((s) => s.days.includes(dayIndex)) || null;
}

/**
 * purgePlaylistRefs — null-out any grid cells referencing the given playlistId.
 * Called after a user playlist is deleted to avoid stale schedule refs.
 * @param {string} playlistId
 */
export function purgePlaylistRefs(playlistId) {
  const id = String(playlistId);
  mutate((state) => ({
    ...state,
    sets: state.sets.map((s) => {
      const newGrid = { ...s.grid };
      let changed = false;
      for (const hour of Object.keys(newGrid)) {
        if (String(newGrid[hour]) === id) {
          delete newGrid[hour];
          changed = true;
        }
      }
      return changed ? { ...s, grid: newGrid, updatedAt: Date.now() } : s;
    }),
  }));
}

// ─── Test helper ───────────────────────────────────────────────────────

export function __resetForTests() {
  _state = parseFromStorage();
  _listeners.clear();
}

// ─── Hook ──────────────────────────────────────────────────────────────

export function useSchedule() {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const addSetCb = useCallback((days = []) => addSet(days), []);
  const removeSetCb = useCallback((setId) => removeSet(setId), []);
  const toggleDayCb = useCallback((setId, dayIndex) => toggleDay(setId, dayIndex), []);
  const setCellCb = useCallback((setId, hour, plId) => setCell(setId, hour, plId), []);
  const paintCellCb = useCallback((setId, hour, plId) => paintCell(setId, hour, plId), []);
  const clearCellCb = useCallback((setId, hour) => clearCell(setId, hour), []);
  const markLaunchedCb = useCallback(() => markLaunched(), []);
  const setEngineStateCb = useCallback((s) => setEngineState(s), []);
  const resetAllCb = useCallback(() => resetAll(), []);
  const getSetForDayCb = useCallback((dayIndex) => getSetForDay(dayIndex), []);

  return {
    sets: state.sets,
    lastLaunchedAt: state.lastLaunchedAt,
    engineState: state.engineState ?? 'idle',
    addSet: addSetCb,
    removeSet: removeSetCb,
    toggleDay: toggleDayCb,
    setCell: setCellCb,
    paintCell: paintCellCb,
    clearCell: clearCellCb,
    markLaunched: markLaunchedCb,
    setEngineState: setEngineStateCb,
    resetAll: resetAllCb,
    getSetForDay: getSetForDayCb,
  };
}
